import React, { Fragment, useMemo, useState, useCallback } from 'react';
import { withTranslation } from 'react-i18next';
import { compose } from 'recompose';
import { Typography } from '@material-ui/core';
import { Alert } from 'Components';
import { changePasswordPublicGateway } from 'Services/changePasswordPublicGateway';
import { browserHistory } from 'Helper/history';
import { PublicRouter } from 'Navigation/Router';
import { RecaptchaLayout } from 'Containers/RecaptchaLayout';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import PasswordChangeForm from 'Components/PasswordChangeForm';
import { validator } from 'Helper/validate';
import { usePasswordPolicy } from 'Helper/usePasswordPolicy';
import { handlePasswordChangeApiError } from 'Helper/passwordChangeErrorHandler';

const ChangePasswordPublic = ({ t, challenge, authnChallenge, client, oidcContext }) => {
  const [username, setUsername] = useState(oidcContext?.loginHint || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [usernameError, setUsernameError] = useState(false);
  const [currentPasswordError, setCurrentPasswordError] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const [newPasswordSameAsCurrentError, setNewPasswordSameAsCurrentError] = useState(false);
  const [newPasswordBackendError, setNewPasswordBackendError] = useState('');
  const { executeRecaptcha } = useGoogleReCaptcha();

  const recaptchaEnabled = Boolean(client?.recaptchaConfiguration && client.recaptchaConfiguration.enabled);
  const clientId = client?.clientId || '';
  const { passwordPolicy, isLoadingPasswordPolicy } = usePasswordPolicy(client.tenantId);

  const { buttonColor } = useMemo(() => {
    const { buttonColor: themeBtn } = client.theme || {};
    return {
      buttonColor: validator.isValidColor(themeBtn) ? themeBtn : undefined,
    };
  }, [client]);

  const handleSubmit = useCallback(async () => {
    const validUsername = validator.isValidUsername(username);
    const validCurrentPassword = !validator.isEmpty(currentPassword);
    const validNew = validator.isValidPassword(newPassword, passwordPolicy);
    const isSameAsCurrentPassword = validCurrentPassword && currentPassword === newPassword;
    const match = newPassword === confirmPassword;

    setUsernameError(!validUsername);
    setCurrentPasswordError(!validCurrentPassword);
    setNewPasswordError(!validNew || isSameAsCurrentPassword);
    setNewPasswordSameAsCurrentError(isSameAsCurrentPassword);
    setConfirmPasswordError(!match);
    setNewPasswordBackendError('');
    if (!validUsername || !validCurrentPassword || !validNew || !match || isSameAsCurrentPassword) return;

    if (!clientId) {
      Alert.error(t('Invalid client'));
      return;
    }

    if (recaptchaEnabled && !executeRecaptcha) {
      Alert.error(t('Unable to validate reCAPTCHA. Please try again.'));
      return;
    }

    setIsLoading(true);
    try {
      const request = {
        username,
        client_id: clientId,
        current_password: currentPassword,
        new_password: newPassword,
      };

      if (recaptchaEnabled) {
        request.recaptcha_token = await executeRecaptcha('PASSWORD_CHANGE');
      }

      await changePasswordPublicGateway(request);
      Alert.success(t('Update Password successfully'));
      browserHistory.push({
        pathname: PublicRouter.Login.url,
        search: `challenge=${challenge}`,
      });
    } catch (error) {
      handlePasswordChangeApiError({ error, t, setUsernameError, setNewPasswordError, setNewPasswordBackendError });
    } finally {
      setIsLoading(false);
    }
  }, [
    username,
    currentPassword,
    newPassword,
    confirmPassword,
    passwordPolicy,
    clientId,
    recaptchaEnabled,
    executeRecaptcha,
    t,
    challenge,
  ]);

  return (
    <Fragment>
      <Typography variant='h5' className='mb-12'>
        {t('Change password')}
      </Typography>

      <PasswordChangeForm
        t={t}
        username={username}
        currentPassword={currentPassword}
        newPassword={newPassword}
        confirmPassword={confirmPassword}
        showPassword={showPassword}
        usernameError={usernameError}
        currentPasswordError={currentPasswordError}
        newPasswordError={newPasswordError}
        confirmPasswordError={confirmPasswordError}
        newPasswordSameAsCurrentError={newPasswordSameAsCurrentError}
        newPasswordBackendError={newPasswordBackendError}
        passwordPolicy={passwordPolicy}
        isLoadingPasswordPolicy={isLoadingPasswordPolicy}
        isLoading={isLoading}
        buttonColor={buttonColor}
        onUsernameChange={(value) => {
          setUsername(value);
          setUsernameError(!validator.isValidUsername(value));
        }}
        onCurrentPasswordChange={(value) => {
          setCurrentPassword(value);
          setCurrentPasswordError(validator.isEmpty(value));
          if (newPassword) {
            const isSameAsCurrentPassword = newPassword === value;
            setNewPasswordError(!validator.isValidPassword(newPassword, passwordPolicy) || isSameAsCurrentPassword);
            setNewPasswordSameAsCurrentError(isSameAsCurrentPassword);
          }
        }}
        onNewPasswordChange={(value) => {
          const isSameAsCurrentPassword = value === currentPassword;
          setNewPassword(value);
          setNewPasswordError(!validator.isValidPassword(value, passwordPolicy) || isSameAsCurrentPassword);
          setNewPasswordSameAsCurrentError(isSameAsCurrentPassword);
          setNewPasswordBackendError('');
          if (confirmPassword) setConfirmPasswordError(value !== confirmPassword);
        }}
        onConfirmPasswordChange={(value) => {
          setConfirmPassword(value);
          setConfirmPasswordError(newPassword !== value);
        }}
        onToggleShowPassword={() => setShowPassword((v) => !v)}
        onSubmit={handleSubmit}
        loginLinkTo={{
          pathname: PublicRouter.Login.url,
          search: `challenge=${challenge}`,
        }}
      />
    </Fragment>
  );
};

export default compose(
  withTranslation(),
  RecaptchaLayout()
)(ChangePasswordPublic);
